import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { sessionHours, fmtHours } from "@/lib/format";
import { Clock, Loader2, Users } from "lucide-react";

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—");
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const dayLabel = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};

export default function Timbrature() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const now = new Date();
  const [summaryMonth, setSummaryMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);

  const load = () => { setLoading(true); api.listTimbrature({}).then((d) => { setItems(d); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  const shown = date ? items.filter((t) => t.date === date) : items;
  const grouped = shown.reduce((acc, t) => { (acc[t.date] = acc[t.date] || []).push(t); return acc; }, {});
  const dates = Object.keys(grouped).sort((a, b) => (a < b ? 1 : -1));

  const monthSummary = useMemo(() => {
    const map = {};
    items.filter((t) => (t.date || "").startsWith(summaryMonth)).forEach((t) => {
      const key = t.name || t.user_email || "—";
      const h = sessionHours(t) || 0;
      if (!map[key]) map[key] = { name: key, hours: 0, sessions: 0 };
      map[key].hours += h; map[key].sessions += 1;
    });
    return Object.values(map).sort((a, b) => b.hours - a.hours);
  }, [items, summaryMonth]);
  const monthTotal = monthSummary.reduce((s, x) => s + x.hours, 0);
  const monthOptions = useMemo(() => {
    const set = new Set(items.map((t) => (t.date || "").slice(0, 7)).filter(Boolean));
    const d = new Date();
    for (let k = 0; k < 3; k++) { const x = new Date(d.getFullYear(), d.getMonth() - k, 1); set.add(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`); }
    set.add(summaryMonth);
    return [...set].sort().reverse();
  }, [items, summaryMonth]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-2xl md:text-3xl font-bold text-slate-100">Timbrature</h1>
          <p className="text-slate-500 text-sm mt-1">Entrate, uscite, mandatini e ore lavorate del personale</p>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="timbrature-date-filter" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-[#121824] border border-[#222D42] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-amber-500/50" />
          {date && <button data-testid="timbrature-clear-date" onClick={() => setDate("")} className="text-sm text-slate-400 hover:text-slate-200 px-2">Azzera</button>}
        </div>
      </div>

      <div data-testid="month-summary-card" className="bg-[#121824] border border-amber-500/20 rounded-xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-head text-base font-semibold text-slate-200 flex items-center gap-2"><Users className="w-4 h-4 text-amber-400" /> Ore lavorate per persona (busta paga)</h3>
          <div className="flex items-center gap-2">
            <select data-testid="summary-month-select" value={summaryMonth} onChange={(e) => setSummaryMonth(e.target.value)} className="bg-[#0d1320] border border-[#222D42] rounded-lg px-3 py-2 text-sm text-slate-200">
              {monthOptions.map((m) => <option key={m} value={m}>{MESI[Number(m.slice(5, 7)) - 1]} {m.slice(0, 4)}</option>)}
            </select>
            <span data-testid="month-total" className="font-mono text-amber-400 font-semibold text-sm">Tot: {fmtHours(monthTotal)}</span>
          </div>
        </div>
        {monthSummary.length === 0 ? (
          <p className="text-slate-600 text-sm">Nessuna timbratura in questo mese.</p>
        ) : (
          <div className="divide-y divide-[#222D42]">
            {monthSummary.map((p) => (
              <div key={p.name} data-testid={`month-person-${p.name}`} className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-200">{p.name}</span>
                <span className="text-xs text-slate-500">{p.sessions} sessioni · <span className="font-mono text-amber-400 text-sm">{fmtHours(p.hours)}</span></span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>
      ) : shown.length === 0 ? (
        <div className="bg-[#121824] border border-[#222D42] rounded-xl p-10 text-center">
          <Clock className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessuna timbratura registrata{date ? " in questa data" : ""}.</p>
        </div>
      ) : (
        dates.map((d) => (
          <div key={d}>
            <div className="flex items-center justify-between mb-2 gap-3">
              <h3 className="font-head text-sm font-semibold text-slate-400 capitalize">{dayLabel(d)}</h3>
              <span data-testid={`day-total-${d}`} className="text-xs font-mono text-amber-400 shrink-0">Totale: {fmtHours(grouped[d].reduce((s, t) => s + (sessionHours(t) || 0), 0))}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {grouped[d].map((t) => {
                const wh = sessionHours(t);
                return (
                  <div key={t.id} data-testid={`timbratura-${t.id}`} className="bg-[#121824] border border-[#222D42] rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-head font-semibold text-slate-100">{t.name || t.user_email}</span>
                      {wh != null && <span className="font-mono text-sm text-amber-400">{fmtHours(wh)}</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="text-slate-500">Entrata <span className="font-mono text-emerald-400">{hhmm(t.clock_in)}</span></span>
                      <span className="text-slate-500">Uscita <span className="font-mono text-sky-400">{hhmm(t.clock_out)}</span></span>
                      {t.pausa_min > 0 && <span className="text-slate-500">Pausa <span className="font-mono text-slate-300">{t.pausa_min}m</span></span>}
                    </div>
                    {t.sede && <p className="text-xs text-slate-400 mt-2"><span className="text-slate-600">Sede:</span> {t.sede}</p>}
                    {t.mandatino && <p className="text-sm text-slate-300 mt-2 bg-[#1A2335] rounded-lg p-2.5 whitespace-pre-wrap">{t.mandatino}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
